import React, { useState } from 'react';
import {
  Box,
  Heading,
  Button,
  FormControl,
  FormLabel,
  Switch,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  Alert,
  AlertIcon,
  Progress,
  VStack,
  HStack,
  Text,
  useDisclosure
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import { format, isValid, parseISO } from 'date-fns';

// Format phone number to display in a readable format
const formatPhoneNumber = (phone) => {
  if (!phone) return '-';
  
  // For display purposes, just clean up any extra spaces but keep the format
  // including "+" for international numbers
  return phone.toString().trim();
};

const formatDate = (dateString, formatStr = 'PPpp') => {
  if (!dateString) return 'N/A';
  try {
    // Parse ISO string to Date object
    const date = parseISO(dateString);
    return isValid(date) ? format(date, formatStr) : 'Invalid date';
  } catch (error) {
    console.error('Date parsing error:', error);
    return 'Invalid date';
  }
};

const ImportLeads = ({ sheet, onImport, loading }) => {
  const [importOptions, setImportOptions] = useState({
    skipDuplicates: true,
    updateExisting: false,
  });
  const [importResults, setImportResults] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const handleImport = async () => {
    try {
      const results = await onImport(importOptions);
      setImportResults(results);
      setShowResults(true);
    } catch (error) {
      console.error('Import failed:', error);
    }
  };

  const renderResultsTable = () => {
    if (!importResults) return null;

    const { results } = importResults;

    return (
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="sm" mb={4}>
            Import Summary
          </Heading>
          <HStack spacing={4}>
            <Tag size="lg" variant="subtle">
              Total: {results.total}
            </Tag>
            <Tag size="lg" colorScheme="green">
              Imported: {results.imported}
            </Tag>
            <Tag size="lg" colorScheme="yellow">
              Skipped: {results.skipped}
            </Tag>
            <Tag size="lg" colorScheme="red">
              Errors: {results.errors.length}
            </Tag>
          </HStack>
        </Box>

        {results.errors.length > 0 && (
          <Box>
            <Heading size="sm" mb={4}>
              Errors
            </Heading>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Row Data</Th>
                  <Th>Error</Th>
                </Tr>
              </Thead>
              <Tbody>
                {results.errors.map((error, index) => (
                  <Tr key={index}>
                    <Td>{error.row.join(', ')}</Td>
                    <Td>{error.error}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}

        {results.importedLeads.length > 0 && (
          <Box>
            <Heading size="sm" mb={4}>
              Imported Leads
            </Heading>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Lead ID</Th>
                  <Th>Name</Th>
                  <Th>Phone</Th>
                  <Th>Added</Th>
                </Tr>
              </Thead>
              <Tbody>
                {results.importedLeads.map((lead) => (
                  <Tr key={lead.leadId}>
                    <Td>{lead.leadId}</Td>
                    <Td>{lead.brideAndGroomName || '-'}</Td>
                    <Td>{formatPhoneNumber(lead.phone)}</Td>
                    <Td>
                      {formatDate(lead.dateAdded, 'PP')}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </VStack>
    );
  };

  if (!sheet?.headerMapping?.phone) {
    return (
      <Alert status="warning">
        <AlertIcon />
        Please configure the header mapping first, ensuring at least the phone number is mapped.
      </Alert>
    );
  }

  return (
    <Box>
      <Heading size="md" mb={4}>
        Import Leads
      </Heading>

      <VStack align="stretch" spacing={4} mb={6}>
        <FormControl display="flex" alignItems="center">
          <FormLabel mb="0">
            Skip Duplicates
          </FormLabel>
          <Switch
            isChecked={importOptions.skipDuplicates}
            onChange={(e) => setImportOptions({
              ...importOptions,
              skipDuplicates: e.target.checked,
              updateExisting: e.target.checked ? false : importOptions.updateExisting
            })}
            isDisabled={loading}
          />
        </FormControl>

        <FormControl display="flex" alignItems="center">
          <FormLabel mb="0">
            Update Existing Leads
          </FormLabel>
          <Switch
            isChecked={importOptions.updateExisting}
            onChange={(e) => setImportOptions({
              ...importOptions,
              updateExisting: e.target.checked,
              skipDuplicates: e.target.checked ? false : importOptions.skipDuplicates
            })}
            isDisabled={loading || importOptions.skipDuplicates}
          />
        </FormControl>
      </VStack>

      <Button
        colorScheme="blue"
        leftIcon={<DownloadIcon />}
        onClick={handleImport}
        isDisabled={loading}
        mb={4}
      >
        Import This Sheet
      </Button>

      {loading && (
        <Progress size="xs" isIndeterminate mt={4} />
      )}

      <Modal
        isOpen={showResults}
        onClose={() => setShowResults(false)}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Import Results</ModalHeader>
          <ModalBody>
            {renderResultsTable()}
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setShowResults(false)}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ImportLeads;
